/*
 * Email logs: pure form/query logic. Limits are pinned to email_enums.json (server's INVALID_TEXT
 * bounds); a reason is additionally required client-side.
 */
const text = (raw) => String(raw === null || raw === undefined ? '' : raw);

// Code points, as the server counts runes (the penalties module's convention).
export const char_length = (raw) => Array.from(text(raw).trim()).length;

export const LIMITS = {
  GREETING_MAX_LEN: 256,
  INTRO_MAX_LEN: 2000,
  FOOTER_MAX_LEN: 2000,
  REASON_MAX_LEN: 1024
};

export const blank_text_form = () => ({ greeting: '', intro: '', footer: '', reason: '' });

const required_error = (raw, label, max) => {
  const n = char_length(raw);
  if (n === 0) return `Enter ${label}.`;
  if (n > max) return `${label} must be at most ${max} characters.`;
  return '';
};

export const reason_error = (raw) => {
  const n = char_length(raw);
  if (n === 0) return 'Say why the text is changing.';
  if (n > LIMITS.REASON_MAX_LEN) {
    return `The reason must be at most ${LIMITS.REASON_MAX_LEN} characters.`;
  }
  return '';
};

export const text_form_errors = (form) => ({
  greeting: required_error(form.greeting, 'a greeting', LIMITS.GREETING_MAX_LEN),
  intro: required_error(form.intro, 'an intro', LIMITS.INTRO_MAX_LEN),
  footer: required_error(form.footer, 'a footer', LIMITS.FOOTER_MAX_LEN),
  reason: reason_error(form.reason)
});

export const has_text_errors = (errors) => Object.values(errors).some(Boolean);

// Client-side filter over the already-loaded teacher rows (small dataset; no server round trip).
export const filter_teacher_rows = (rows, q) => {
  const needle = text(q).trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    (r) =>
      String(r.teacher_name || '')
        .toLowerCase()
        .indexOf(needle) !== -1 ||
      String(r.teacher_slug || '')
        .toLowerCase()
        .indexOf(needle) !== -1
  );
};

export const email_logs_filters_on = (q) => !!text(q).trim();

// The line under the search: how many teachers the list shows for the month, and the search if any.
export const email_logs_filter_summary = ({ total, month_label, q }) => {
  const n = Number(total) || 0;
  const noun = n === 1 ? 'teacher' : 'teachers';
  const month = month_label ? ` for ${month_label}` : '';
  if (!email_logs_filters_on(q)) return { lead: `all ${n} ${noun}`, rest: `${month}.` };
  return { lead: `${n} ${noun}`, rest: `${month}: name or slug matches “${text(q).trim()}”.` };
};

// ---- the URL: sort=name|desc (name ascending is the default, so it is left out) ----
// The name search stays out of the URL: it is a teacher's name.
export const EMAIL_LOGS_URL_KEYS = ['sort'];
export const EMAIL_LOGS_DEFAULT_SORT = { key: 'teacher_name', dir: 'asc' };
const SORT_URL_KEY = { teacher_name: 'name' };

export const parse_email_logs_sort = (search) => {
  const raw = new URLSearchParams(typeof search === 'string' ? search : '').get('sort') || '';
  const [k, dir] = raw.split('|');
  const key = Object.keys(SORT_URL_KEY).find((x) => SORT_URL_KEY[x] === k);
  return key && (dir === 'asc' || dir === 'desc') ? { key, dir } : EMAIL_LOGS_DEFAULT_SORT;
};

export const email_logs_query = (sort) => {
  const s = sort || EMAIL_LOGS_DEFAULT_SORT;
  const k = SORT_URL_KEY[s.key];
  const is_default = s.key === EMAIL_LOGS_DEFAULT_SORT.key && s.dir === EMAIL_LOGS_DEFAULT_SORT.dir;
  return { sort: k && !is_default ? `${k}|${s.dir}` : '' };
};
